import React from 'react';
import cn from 'classnames';

import { Text } from 'components/common/Elements/Elements';

import classes from 'components/Product/FeaturesFlag/AssignedCustomers/parts/ResultAlert/ResultAlert.module.scss';

interface FlagResultAlertProps {
  tenantName: string;
  mode: 'Assigned' | 'Unassigned';
  succeeded: number;
  failed: number;
  /** Named instead of counted when the action was about one flag (unassign). */
  flagName?: string;
  /** Distinct server messages, e.g. the GA guard's 409. */
  reasons?: string[];
}

/**
 * The production `ResultAlert` is deliberately NOT reused here: its sentence
 * runs the other way round — "The {feature} feature has been ... to {N}
 * customers" — because the global page assigns one flag to many tenants. This
 * page assigns many flags to one tenant, so passing the tenant as `name` read
 * "The Acme Corp feature has been successfully Assigned to 3 customers".
 *
 * Same stylesheet, so it looks identical; only the direction differs.
 */
const FlagResultAlert = ({
  tenantName,
  mode,
  succeeded,
  failed,
  flagName,
  reasons = []
}: FlagResultAlertProps) => {
  const ok = succeeded > 0;
  const total = succeeded + failed;
  const preposition = mode === 'Assigned' ? 'to' : 'from';

  const count = (n: number) => `${n} feature flag${n === 1 ? '' : 's'}`;
  // On a partial success the sentence reads "2 of 3 feature flags", so the
  // subject is the total; otherwise it is whichever side happened.
  const shown = ok ? (failed ? total : succeeded) : failed;
  // One named flag reads better than "1 feature flag", and the unassign flow
  // always has exactly one.
  const subject = flagName || count(shown);
  const verb = flagName || shown === 1 ? 'has' : 'have';

  const tenant = (
    <Text className={cn(classes.platform, ok ? classes.blue : classes.red)}>
      {` ${tenantName} `}
    </Text>
  );

  return (
    <div className={classes.result}>
      {ok ? (
        <Text className="">
          {failed ? `${succeeded} of ` : ''}
          {subject} {verb} been successfully {mode.toLowerCase()} {preposition}
          {tenant}
          {failed ? <Text className="">{`(${failed} failed)`}</Text> : null}
        </Text>
      ) : (
        <Text className="">
          {subject} could not be {mode.toLowerCase()} {preposition}
          {tenant}
          {reasons.length ? (
            <Text className="">{`— ${reasons.join('; ')}`}</Text>
          ) : null}
        </Text>
      )}
    </div>
  );
};

export default FlagResultAlert;
